/**
 * Configuration of the Digitale Menschen support widget, as far as it is an
 * environment concern: the endpoints, the key that identifies the app to the
 * support backend, and where the launcher sits.
 *
 * Everything the user reads — the launcher's label, the form's title and
 * subtitle — is deliberately **not** here. Those are `$localize` messages in
 * `SupportWidgetService`, because this library is not translated and a German
 * literal in an environment file cannot be.
 */
export interface SupportWidgetConfig {
  /** Identifies the app to the support backend. Issued per app by Digitale Menschen. */
  apiKey: string;
  /** Where the widget posts a request. */
  apiBaseUrl: string;
  /** The loader script the widget is bootstrapped from. */
  loaderUrl: string;
  /** Shown in the support backend, and interpolated into the form's title. */
  appName: string;
  /**
   * Named spot of the launcher. Its colours come from the `--dm-support-*` tokens in
   * `theme/global.scss`, not from here.
   */
  position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  /** Shape of the launcher; the edge only applies to the tab. */
  launcher: { variant: 'tab' | 'button'; edge?: 'top' | 'right' | 'bottom' | 'left' };
  /**
   * Who the widget loads for: `everyone`, signed in or not, or only signed-in Neosys
   * users (`istNeosysAdmin`), so customers in production never see it.
   */
  audience: 'everyone' | 'neosys';
  /**
   * How long a loaded widget version is cached for. The loader URL carries the
   * current timestamp divided by this window, so every window busts the cache
   * once and every load inside one is served from it.
   */
  cacheWindowMs: number;
}

/**
 * The support configuration both apps share, with only `appName` differing.
 * The production values; other environments widen `audience`.
 *
 * A function rather than a constant so each app gets its own `launcher` object.
 *
 * @param {string} appName - How this app is named in the support backend and in
 *   the widget's title.
 * @returns {SupportWidgetConfig} A fresh configuration for that app.
 */
export function createSupportWidgetConfig(appName: string): SupportWidgetConfig {
  return {
    apiKey: 'dm_500f876ce885f517f925920c54c823e617e15d8770ae9a6b',
    apiBaseUrl: 'https://support.digitalemenschen.app/api',
    loaderUrl: 'https://support.digitalemenschen.app/support-widget.js',
    appName,
    position: 'bottom-right',
    launcher: { variant: 'button' },
    audience: 'neosys',
    cacheWindowMs: 60 * 60 * 1000,
  };
}
